import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { findCardById } from '@yadviga/shared'
import {
  api,
  ApiError,
  type SpreadCategory,
  type SpreadKind,
  type SpreadResultPayload,
} from '../../api/client'
import { TarotFlipCard } from '../../components/tarot/TarotFlipCard'
import { Button } from '../../components/ui/Button'
import { BackButton } from '../../components/ui/BackButton'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { usePaywallStore } from '../../store/paywall.store'

const POSITION_KEYS: Record<SpreadKind, string[]> = {
  '3card': ['tarot.pos_3_past', 'tarot.pos_3_present', 'tarot.pos_3_future'],
  '5card_relationship': [
    'tarot.pos_5_you',
    'tarot.pos_5_partner',
    'tarot.pos_5_bond',
    'tarot.pos_5_obstacle',
    'tarot.pos_5_direction',
  ],
  celtic_cross: [
    'tarot.pos_celtic_heart',
    'tarot.pos_celtic_cross',
    'tarot.pos_celtic_foundation',
    'tarot.pos_celtic_past',
    'tarot.pos_celtic_possible_future',
    'tarot.pos_celtic_near_future',
    'tarot.pos_celtic_you_now',
    'tarot.pos_celtic_environment',
    'tarot.pos_celtic_hopes_fears',
    'tarot.pos_celtic_outcome',
  ],
}

const CATEGORIES: SpreadCategory[] = ['love', 'career', 'decision', 'growth', 'general']
const CARD_COUNT: Record<SpreadKind, number> = { '3card': 3, '5card_relationship': 5, celtic_cross: 10 }

type Phase = 'form' | 'shuffling' | 'revealing' | 'done'

export function TarotSpreadRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { kind } = useParams<{ kind: SpreadKind }>()
  const [phase, setPhase] = useState<Phase>('form')
  const [question, setQuestion] = useState('')
  const [category, setCategory] = useState<SpreadCategory>('general')
  const [result, setResult] = useState<SpreadResultPayload | null>(null)
  const [revealed, setRevealed] = useState<Set<number>>(new Set())
  const showPaywall = usePaywallStore((s) => s.show)

  if (!kind || !(kind in CARD_COUNT)) {
    return <p className="py-10 text-center text-sage-600">Unknown spread kind</p>
  }
  const positions = POSITION_KEYS[kind].map((k) => t(k))
  const cardCount = CARD_COUNT[kind]

  const [spreadError, setSpreadError] = useState<string | null>(null)
  const createSpread = useMutation({
    mutationFn: () => api.createSpread({ kind, question: question || undefined, category }),
    onSuccess: (data) => {
      setResult(data)
      setSpreadError(null)
      setPhase('revealing')
    },
    onError: (err) => {
      setPhase('form')
      // 402 has its own UX path (paywall popup), no need to surface as error.
      if (err instanceof ApiError && err.status === 402) {
        const detail = err.details
        showPaywall({
          reason: (detail?.reason as 'feature_locked' | 'free_quota_exhausted') ?? 'feature_locked',
          feature: detail?.feature as string | undefined,
        })
        return
      }
      // Everything else — show the user a clear message instead of silently
      // bouncing them back to the form. AiUnavailable (503) is the typical
      // case: all chain providers down, retry usually works after 30 sec.
      const msg =
        err instanceof ApiError
          ? err.body.error.message
          : (err as Error).message
      const friendly =
        err instanceof ApiError && err.status >= 500
          ? t('tarot.error_ai_unavailable')
          : msg || t('tarot.error_generic')
      setSpreadError(friendly)
      toast.error(friendly)
    },
  })

  function startShuffle() {
    setPhase('shuffling')
    setRevealed(new Set())
    setTimeout(() => createSpread.mutate(), 1500)
  }

  function reveal(idx: number) {
    setRevealed((prev) => {
      const next = new Set(prev)
      next.add(idx)
      if (next.size === cardCount) {
        // Small delay so the last card finishes its flip before the layout
        // shrinks the cards and slides in the interpretation panel.
        setTimeout(() => setPhase('done'), 800)
      }
      return next
    })
  }

  // Preload all card faces (and the deck back) the moment the spread arrives,
  // so by the time the user taps a card the image is already in the browser
  // cache. Avoids the flash of "broken image" between facedown and reveal.
  useEffect(() => {
    if (!result?.cards) return
    const back = new Image()
    back.src = '/cards/card_back.webp'
    for (const c of result.cards) {
      const card = findCardById(c.id)
      if (!card) continue
      const img = new Image()
      img.src = `/cards/${card.imageKey}.webp`
    }
  }, [result])

  if (phase === 'form') {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <header>
          <BackButton to="/tarot" />
        </header>

        <div>
          <h2 className="font-serif text-2xl text-sage-800">{t('tarot.question_title')}</h2>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, 280))}
            placeholder={t('tarot.question_placeholder')}
            rows={3}
            className="mt-2 w-full rounded-2xl border border-sage-200 bg-white px-3 py-2 text-sage-800 outline-none focus:border-sage-500"
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm text-sage-700">{t('tarot.category')}</h3>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`rounded-full px-3 py-1 text-sm transition ${
                  category === c
                    ? 'bg-sage-600 text-cream'
                    : 'bg-white text-sage-700 ring-1 ring-sage-200 hover:bg-sage-50'
                }`}
              >
                {t(`tarot.cat_${c}`)}
              </button>
            ))}
          </div>
        </div>

        {spreadError && (
          <div className="rounded-2xl bg-[#fde8e3] p-4 ring-1 ring-[#f0bfb7]/60">
            <p className="text-sm leading-relaxed text-copper">{spreadError}</p>
            <p className="mt-1 text-xs text-sage-600">{t('tarot.error_retry_hint')}</p>
          </div>
        )}

        <Button size="lg" onClick={startShuffle}>
          {t('actions.continue')}
        </Button>
      </div>
    )
  }

  if (phase === 'shuffling' || (phase === 'revealing' && !result)) {
    return (
      <div className="flex min-h-[60dvh] flex-col items-center justify-center">
        <LoadingDots pose="tarot" />
      </div>
    )
  }

  const compact = phase === 'done'

  // Card index per row when laid out in a "spread" shape during reveal. The
  // pattern stretches the spread vertically so the artwork can breathe and
  // the layout feels like a real ritual, not a tight grid:
  //  • 3-card        → triangle: 1 on top (centre = "Настоящее"),
  //                              2 below (left = past, right = future)
  //  • 5-card        → pyramid: 1-2-2 with the "link" at the apex
  //  • celtic_cross  → 4-4-2 stacked rows, more compact since there are 10
  const REVEAL_ROWS: Record<SpreadKind, number[][]> = {
    '3card': [[1], [0, 2]],
    '5card_relationship': [[2], [0, 1], [3, 4]],
    celtic_cross: [[0, 1, 2, 3], [4, 5, 6, 7], [8, 9]],
  }

  // Bigger cards during reveal (more vertical room thanks to the row layout),
  // smaller in compact mode where the interpretation needs space below.
  const revealCardWidth: Record<SpreadKind, string> = {
    '3card': 'w-36 sm:w-40',
    '5card_relationship': 'w-28 sm:w-32',
    celtic_cross: 'w-[72px] sm:w-20',
  }
  const compactCardWidth: Record<SpreadKind, string> = {
    '3card': 'w-20',
    '5card_relationship': 'w-[68px]',
    celtic_cross: 'w-14',
  }
  const cardWidth = compact ? compactCardWidth[kind] : revealCardWidth[kind]

  function renderCard(cardIdx: number) {
    const c = result!.cards[cardIdx]!
    const label = positions[cardIdx] ?? `#${cardIdx + 1}`
    return (
      <div key={cardIdx} className="flex flex-col items-center gap-1">
        <TarotFlipCard
          cardId={c.id}
          reversed={c.reversed}
          revealed={revealed.has(cardIdx)}
          onReveal={() => reveal(cardIdx)}
          className={cardWidth}
          positionLabel={label}
        />
        <span className="text-center text-[11px] leading-tight text-sage-600">{label}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <AnimatePresence>
        {compact && (
          <motion.header
            key="header"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between"
          >
            <BackButton to="/tarot" />
          </motion.header>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!compact ? (
          <motion.div
            key="reveal-stage"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex min-h-[60dvh] flex-col items-center justify-center gap-4"
          >
            <p className="text-center text-sage-700">{t('tarot.tap_to_reveal')}</p>
            <div className="flex w-full max-w-[460px] flex-col items-center gap-3 sm:gap-4">
              {REVEAL_ROWS[kind].map((row, ri) => (
                <div key={ri} className="flex flex-row items-end justify-center gap-3 sm:gap-4">
                  {row.map((cardIdx) => renderCard(cardIdx))}
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="done-stage"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col gap-5"
          >
            <div className="flex flex-row flex-wrap justify-center gap-2 sm:gap-3">
              {result?.cards.map((_, i) => renderCard(i))}
            </div>
            {result?.interpretation && (
              <motion.section
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.4 }}
              >
                <h3 className="mb-3 font-serif text-lg text-sage-800">{t('tarot.reading')}</h3>
                <SectionedMarkdown>{result.interpretation}</SectionedMarkdown>
              </motion.section>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
